import {
  ArrowRight,
  BarChart3,
  ClipboardList,
  FileSignature,
  Globe,
  LogIn,
  MapPinned,
  MessageCircle,
  Phone,
  Smartphone,
  Target,
  Timer,
  UsersRound,
  Wallet,
  Waypoints,
} from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './landing.css';
import { LandingDemo } from './LandingDemo';
import {
  annualPrice,
  COMPANY_SITE,
  CUSTOMER_CARE,
  demoWhatsAppLink,
  FAQS,
  FEATURE_GROUPS,
  kes,
  missingDemoFields,
  PAYMENT_METHODS,
  PRICING,
  STEPS,
  TEAM_SIZES,
  type DemoRequest,
} from './landing-data';

export { CUSTOMER_CARE } from './landing-data';

/**
 * The front door, for somebody who is not signed in — and the sales page.
 *
 * Three kinds of people arrive here. ISP owners and managers deciding whether
 * to use Dispatch, who get the whole pitch: what it does, a board to play
 * with, prices and a way to book a demo. Staff, who need one obvious way in
 * (Staff sign in, top right). And customers given this address with a job,
 * who want to know where their technician is — the tracking box sits straight
 * under the first screen for them, with the care number beside it.
 *
 * Signing in lives at /login, so staff who bookmark it never see this page.
 * Copy, prices and the feature list live in landing-data.ts.
 */

const PAGE_TITLE = 'GoSpeedNet Dispatch — field service software for ISPs in Kenya';

/** Accepts a tracking code, or the whole link it came in, and finds the code in it. */
export function trackingCode(input: string): string | null {
  const match = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.exec(input.trim());
  return match ? match[0].toLowerCase() : null;
}

/* ── Colour theme ─────────────────────────────────────────────────────────── */

export const THEMES = ['classic', 'dark', 'light'] as const;
export type Theme = (typeof THEMES)[number];
/** Read before first paint by public/theme-boot.js; keep the two in step. */
export const THEME_KEY = 'gsn-dispatch-theme';

/** Whatever was stored, as a theme. Anything unexpected is Classic. */
export function themeFrom(value: string | null | undefined): Theme {
  return value === 'dark' || value === 'light' ? value : 'classic';
}

function useTheme(): [Theme, (theme: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(() =>
    typeof document === 'undefined' ? 'classic' : themeFrom(document.documentElement.getAttribute('data-theme')),
  );

  function choose(next: Theme) {
    setTheme(next);
    // Classic is the absence of a choice, so it carries no attribute.
    if (next === 'classic') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', next);
    try {
      if (next === 'classic') window.localStorage.removeItem(THEME_KEY);
      else window.localStorage.setItem(THEME_KEY, next);
    } catch {
      // Storage blocked: the choice lasts for this visit only.
    }
  }

  return [theme, choose];
}

const THEME_NAMES: Record<Theme, string> = {
  classic: 'Classic',
  dark: 'Dark',
  light: 'Light',
};

function ThemeSwitch({ theme, onChange, id }: { theme: Theme; onChange: (theme: Theme) => void; id: string }) {
  return (
    <div className="theme-switch" role="group" aria-labelledby={`${id}-label`}>
      <span id={`${id}-label`} className="sr">
        Colour theme
      </span>
      {THEMES.map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={theme === option}
          onClick={() => onChange(option)}
          title={`${THEME_NAMES[option]} theme`}
        >
          <span className={`swatch ${option}`} aria-hidden="true" />
          <span className="tl">{THEME_NAMES[option]}</span>
          <span className="sr"> theme</span>
        </button>
      ))}
    </div>
  );
}

/* ── The page ─────────────────────────────────────────────────────────────── */

const GROUP_ICONS: Record<string, typeof ClipboardList> = {
  dispatch: ClipboardList,
  field: Smartphone,
  customer: MapPinned,
  sla: Timer,
  money: Wallet,
  contracts: FileSignature,
  selling: Target,
  network: Waypoints,
  people: UsersRound,
  numbers: BarChart3,
};

const EMPTY_REQUEST: DemoRequest = {
  name: '',
  company: '',
  phone: '',
  email: '',
  teamSize: '',
  plan: '',
};

const FIELD_ERRORS: Partial<Record<keyof DemoRequest, string>> = {
  name: 'Tell us your name.',
  company: 'Tell us the company name.',
  phone: 'A phone number we can call, so we can arrange the demo.',
};

export function LandingPage() {
  const navigate = useNavigate();
  const [theme, setTheme] = useTheme();

  // Tracking box.
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Pricing and booking.
  const [annual, setAnnual] = useState(false);
  const [request, setRequest] = useState<DemoRequest>(EMPTY_REQUEST);
  const [missing, setMissing] = useState<(keyof DemoRequest)[]>([]);
  const [sentLink, setSentLink] = useState<string | null>(null);
  const nameInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const previous = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = previous;
    };
  }, []);

  function track(event: FormEvent) {
    event.preventDefault();
    const found = trackingCode(code);
    if (!found) {
      setError('Paste the tracking link from your SMS or email, or the code at the end of it.');
      return;
    }
    navigate(`/track/${found}`);
  }

  function update<K extends keyof DemoRequest>(key: K, value: DemoRequest[K]) {
    setRequest((current) => ({ ...current, [key]: value }));
    setMissing((current) => current.filter((field) => field !== key));
    setSentLink(null);
  }

  function choosePlan(plan: string) {
    update('plan', plan);
    document.getElementById('book')?.scrollIntoView({ block: 'start' });
    nameInput.current?.focus({ preventScroll: true });
  }

  function book(event: FormEvent) {
    event.preventDefault();
    const gaps = missingDemoFields(request);
    setMissing(gaps);
    if (gaps.length > 0) {
      document.getElementById(`book-${gaps[0]}`)?.focus();
      return;
    }
    const link = demoWhatsAppLink(request);
    setSentLink(link);
    window.open(link, '_blank', 'noopener,noreferrer');
  }

  const invalid = (field: keyof DemoRequest) => missing.includes(field);

  return (
    <div className="mk">
      <a className="skip" href="#main">
        Skip to content
      </a>

      <header className="top">
        <nav className="wrap nav" aria-label="Main">
          <a className="brand" href="#main" aria-label="GoSpeedNet Dispatch, top of page">
            <img src="/brand/logo.png" alt="" className="brand-mark" />
            <span className="word">Dispatch</span>
          </a>
          <a className="sec" href="#features">
            Features
          </a>
          <a className="sec" href="#demo">
            Demo
          </a>
          <a className="sec" href="#pricing">
            Pricing
          </a>
          <a className="sec" href="#faq">
            FAQ
          </a>
          <a className="sec" href="#track">
            Track a job
          </a>
          <ThemeSwitch theme={theme} onChange={setTheme} id="theme-top" />
          <Link to="/login" className="signin">
            <LogIn size={14} aria-hidden="true" />
            <span>Staff sign in</span>
          </Link>
          <span className="hide-phone">
            <a className="btn small" href="#book">
              Book a demo
            </a>
          </span>
        </nav>
      </header>

      <main id="main">
        {/* ── Hero ── */}
        <div className="hero">
          <div className="wrap frame">
            <span className="mark tl" aria-hidden="true" />
            <span className="mark tr" aria-hidden="true" />
            <div className="hero-main">
              <div className="hero-copy">
                <p className="kicker">
                  <span>Field service for ISPs</span>
                  <span>M-Pesa</span>
                  <span>Works offline</span>
                </p>
                <h1>
                  Every job, from the first call to <em>fixed.</em>
                </h1>
                <p className="lead">
                  Dispatch puts every installation, fault and visit on one board, sends each job to the technician’s
                  phone, keeps your customers informed, and bills them by M-Pesa. Built by GoSpeedNet to run our own
                  network in Kisii — now available to other internet providers.
                </p>
                <div className="actions">
                  <a className="btn" href="#book">
                    Book a demo <ArrowRight size={16} aria-hidden="true" />
                  </a>
                  <a className="btn ghost" href="#pricing">
                    See pricing
                  </a>
                </div>
              </div>
              <div className="hero-side">
                {/* A job's four states on a turning cube. Decorative; still for people who prefer less motion. */}
                <div className="stage" aria-hidden="true">
                  <div className="cube">
                    <div className="face f1">
                      <i>01</i>
                      <b>LOGGED</b>
                    </div>
                    <div className="face f2">
                      <i>02</i>
                      <b>ASSIGNED</b>
                    </div>
                    <div className="face f3">
                      <i>03</i>
                      <b>ON SITE</b>
                    </div>
                    <div className="face f4">
                      <i>04</i>
                      <b>FIXED</b>
                    </div>
                    <div className="face top" />
                    <div className="face bottom" />
                  </div>
                </div>
                <div className="panel">
                  <p className="label">What every job gets</p>
                  <ul className="points">
                    <li>An owner, a priority and an SLA clock</li>
                    <li>A job link on the technician’s phone</li>
                    <li>Photos and a signature from site</li>
                    <li>Updates and a tracking link for the customer</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Customers waiting on a job ── */}
        <div id="track" className="trackwrap">
          <div className="wrap trackband">
            <div>
              <h2>Waiting for a technician?</h2>
              <p>Paste the tracking link we sent you to see where your job has reached and when to expect us.</p>
              <p className="care">
                <a href={CUSTOMER_CARE.tel}>
                  <Phone size={16} aria-hidden="true" />
                  Customer care: {CUSTOMER_CARE.display}
                </a>
                <a href={CUSTOMER_CARE.whatsapp} target="_blank" rel="noopener noreferrer">
                  <MessageCircle size={16} aria-hidden="true" />
                  WhatsApp us
                </a>
              </p>
            </div>
            <div>
              <form onSubmit={track} className="inline-form" noValidate>
                <label htmlFor="track-code" className="sr">
                  Tracking link or code
                </label>
                <input
                  id="track-code"
                  className="i"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    setError(null);
                  }}
                  placeholder="https://dispatch.gospeednet.co.ke/track/…"
                  autoComplete="off"
                  inputMode="url"
                  aria-invalid={error !== null}
                  aria-describedby={error ? 'track-error' : undefined}
                />
                <button type="submit" className="btn ghost">
                  Track
                </button>
              </form>
              {error && (
                <p id="track-error" role="alert" className="err">
                  {error}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── Features ── */}
        <section className="s" id="features" aria-labelledby="features-t">
          <div className="wrap">
            <div className="s-head">
              <p className="kicker">
                <span>Features</span>
                <span>{FEATURE_GROUPS.length} parts, one system</span>
              </p>
              <h2 className="t" id="features-t">
                Everything between the call and the payment
              </h2>
              <p className="sub">
                The office, the field team, sales and accounts work from the same records — so a job, a customer and an
                invoice are never three different stories.
              </p>
            </div>
            <div className="grid">
              {FEATURE_GROUPS.map((group) => {
                const Icon = GROUP_ICONS[group.id] ?? ClipboardList;
                return (
                  <article className="cell" key={group.id}>
                    <p className="code">
                      <span>{group.code}</span>
                      <Icon size={18} aria-hidden="true" />
                    </p>
                    <h3>{group.title}</h3>
                    <p>{group.lead}</p>
                    <ul className="ticks">
                      {group.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── How it works ── */}
        <section className="s" id="how" aria-labelledby="how-t">
          <div className="wrap">
            <div className="s-head">
              <p className="kicker">
                <span>How it works</span>
              </p>
              <h2 className="t" id="how-t">
                One job, start to finish
              </h2>
              <p className="sub">The same five steps for an installation, a fault or a site visit.</p>
            </div>
            <ol className="steps">
              {STEPS.map((step) => (
                <li key={step.title}>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ── Demo board ── */}
        <section className="s" id="demo" aria-labelledby="demo-t">
          <div className="wrap">
            <div className="s-head">
              <p className="kicker">
                <span>Try it</span>
                <span>Demo</span>
              </p>
              <h2 className="t" id="demo-t">
                Move a job across the board
              </h2>
              <p className="sub">
                A small copy of the dispatch board with sample jobs. Click a card to move it on and see what its
                customer is sent at each stage.
              </p>
            </div>
            <LandingDemo />
          </div>
        </section>

        {/* ── Pricing ── */}
        <section className="s" id="pricing" aria-labelledby="pricing-t">
          <div className="wrap">
            <div className="s-head">
              <p className="kicker">
                <span>Pricing</span>
                <span>{PRICING.currency} per month</span>
              </p>
              <h2 className="t" id="pricing-t">
                Three packages, priced in shillings
              </h2>
              <p className="sub">
                Pay monthly, or pay for a year and get {12 - PRICING.annualMonthsCharged} months free. Every package
                includes setting up your areas, job types and staff logins.
              </p>
            </div>

            <div className="seg" role="group" aria-label="Billing period">
              <button type="button" aria-pressed={!annual} onClick={() => setAnnual(false)}>
                Monthly
              </button>
              <button type="button" aria-pressed={annual} onClick={() => setAnnual(true)}>
                Annual · {12 - PRICING.annualMonthsCharged} months free
              </button>
            </div>

            <div className="plans">
              {PRICING.plans.map((plan, index) => {
                const year = annualPrice(plan.monthlyKes);
                const featured = 'featured' in plan && plan.featured;
                return (
                  <article
                    className={`cell plan${featured ? ' featured' : ''}`}
                    key={plan.id}
                    aria-labelledby={`plan-${plan.id}`}
                  >
                    <p className="model">
                      <span>Package {String(index + 1).padStart(2, '0')}</span>
                      {featured && <span>Recommended</span>}
                    </p>
                    <h3 id={`plan-${plan.id}`}>{plan.name}</h3>
                    <p className="price">
                      {plan.fromPrice && <small>from</small>}
                      {annual ? kes(year.total) : kes(plan.monthlyKes)}
                      <small>{annual ? 'per year' : 'per month'}</small>
                    </p>
                    <p className="note">
                      {annual
                        ? `Saves ${kes(year.saving)} on paying monthly.`
                        : `Or ${kes(year.total)} a year, paid up front.`}
                    </p>
                    <p>{plan.forWho}</p>
                    <ul className="limits">
                      {plan.limits.map((limit) => (
                        <li key={limit}>{limit}</li>
                      ))}
                    </ul>
                    <ul className="ticks">
                      {plan.features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className={featured ? 'btn' : 'btn ghost'}
                      onClick={() => choosePlan(plan.id)}
                    >
                      Book a demo of {plan.name}
                    </button>
                  </article>
                );
              })}
            </div>
            <p className="note after-plans">
              Prices are per month in Kenya shillings. Technician and job numbers are what each package is sized for;
              tell us if you are close to a limit and we will suggest the right one.
            </p>
          </div>
        </section>

        {/* ── How you pay ── */}
        <section className="s" id="pay" aria-labelledby="pay-t">
          <div className="wrap">
            <div className="s-head">
              <p className="kicker">
                <span>How you pay</span>
              </p>
              <h2 className="t" id="pay-t">
                Monthly or annual, the way you already pay
              </h2>
              <p className="sub">
                We send an invoice at the start of each period. Payment details are printed on the invoice — if anybody
                asks you to pay to a number that is not on it, call us first.
              </p>
            </div>
            <div className="grid">
              {PAYMENT_METHODS.map((method, index) => (
                <article className="cell" key={method.title}>
                  <p className="code">
                    <span>Option {String(index + 1).padStart(2, '0')}</span>
                  </p>
                  <h3>{method.title}</h3>
                  <p>{method.body}</p>
                </article>
              ))}
              <article className="cell">
                <p className="code">
                  <span>Billing period</span>
                </p>
                <h3>Monthly or annual</h3>
                <p>
                  Monthly invoices, or one a year: twelve months for the price of {PRICING.annualMonthsCharged}. Change
                  package at the start of any billing month.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* ── Book a demo ── */}
        <section className="s" id="book" aria-labelledby="book-t">
          <div className="wrap">
            <div className="book">
              <div className="book-intro">
                <p className="kicker">
                  <span>Get started</span>
                </p>
                <h2 className="t" id="book-t">
                  Book a demo
                </h2>
                <p className="sub">
                  We will walk you through Dispatch with your own job types and areas, and answer questions about moving
                  over from what you use now.
                </p>
                <ul className="contact-list">
                  <li>
                    <a href={CUSTOMER_CARE.tel}>
                      <Phone size={18} aria-hidden="true" /> Call {CUSTOMER_CARE.display}
                    </a>
                  </li>
                  <li>
                    <a href={CUSTOMER_CARE.whatsapp} target="_blank" rel="noopener noreferrer">
                      <MessageCircle size={18} aria-hidden="true" /> WhatsApp {CUSTOMER_CARE.display}
                    </a>
                  </li>
                  <li>
                    <a href={COMPANY_SITE}>
                      <Globe size={18} aria-hidden="true" /> gospeednet.co.ke
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <form onSubmit={book} noValidate className="fields" aria-describedby="book-how">
                  <div className="row2">
                    <label className="f" htmlFor="book-name">
                      Your name
                      <input
                        id="book-name"
                        ref={nameInput}
                        className="i"
                        autoComplete="name"
                        value={request.name}
                        onChange={(e) => update('name', e.target.value)}
                        aria-invalid={invalid('name')}
                        aria-describedby={invalid('name') ? 'book-name-err' : undefined}
                        required
                      />
                    </label>
                    <label className="f" htmlFor="book-company">
                      Company
                      <input
                        id="book-company"
                        className="i"
                        autoComplete="organization"
                        value={request.company}
                        onChange={(e) => update('company', e.target.value)}
                        aria-invalid={invalid('company')}
                        aria-describedby={invalid('company') ? 'book-company-err' : undefined}
                        required
                      />
                    </label>
                  </div>
                  <div className="row2">
                    <label className="f" htmlFor="book-phone">
                      Phone
                      <input
                        id="book-phone"
                        className="i"
                        type="tel"
                        autoComplete="tel"
                        inputMode="tel"
                        placeholder="07…"
                        value={request.phone}
                        onChange={(e) => update('phone', e.target.value)}
                        aria-invalid={invalid('phone')}
                        aria-describedby={invalid('phone') ? 'book-phone-err' : undefined}
                        required
                      />
                    </label>
                    <label className="f" htmlFor="book-email">
                      Email (optional)
                      <input
                        id="book-email"
                        className="i"
                        type="email"
                        autoComplete="email"
                        value={request.email}
                        onChange={(e) => update('email', e.target.value)}
                      />
                    </label>
                  </div>
                  <div className="row2">
                    <label className="f" htmlFor="book-teamSize">
                      Team size
                      <select
                        id="book-teamSize"
                        className="i"
                        value={request.teamSize}
                        onChange={(e) => update('teamSize', e.target.value)}
                      >
                        <option value="">Choose…</option>
                        {TEAM_SIZES.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="f" htmlFor="book-plan">
                      Package
                      <select
                        id="book-plan"
                        className="i"
                        value={request.plan}
                        onChange={(e) => update('plan', e.target.value)}
                      >
                        <option value="">Not sure yet</option>
                        {PRICING.plans.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {`${plan.name} — ${plan.fromPrice ? 'from ' : ''}${kes(plan.monthlyKes)} a month`}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {missing.length > 0 && (
                    <div role="alert">
                      {missing.map((field) => (
                        <p className="err" id={`book-${field}-err`} key={field}>
                          {FIELD_ERRORS[field]}
                        </p>
                      ))}
                    </div>
                  )}

                  <div className="actions">
                    <button type="submit" className="btn">
                      <MessageCircle size={16} aria-hidden="true" /> Send by WhatsApp
                    </button>
                    <a className="btn ghost" href={CUSTOMER_CARE.tel}>
                      <Phone size={16} aria-hidden="true" /> Or call us
                    </a>
                  </div>
                  <p className="note" id="book-how">
                    This opens WhatsApp with your details written out, ready to send to {CUSTOMER_CARE.display}. Nothing
                    is sent or stored until you press send there.
                  </p>

                  {sentLink && (
                    <div className="done" role="status">
                      <p>
                        WhatsApp should have opened with your request. Press send there and we will reply to arrange a
                        time.
                      </p>
                      <p className="note">
                        Didn’t open?{' '}
                        <a href={sentLink} target="_blank" rel="noopener noreferrer">
                          Open WhatsApp again
                        </a>{' '}
                        or call <a href={CUSTOMER_CARE.tel}>{CUSTOMER_CARE.display}</a>.
                      </p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="s" id="faq" aria-labelledby="faq-t">
          <div className="wrap">
            <div className="s-head">
              <p className="kicker">
                <span>Questions</span>
              </p>
              <h2 className="t" id="faq-t">
                Asked before buying
              </h2>
            </div>
            {FAQS.map((faq) => (
              <details className="q" key={faq.q}>
                <summary>{faq.q}</summary>
                <p>{faq.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ── Final call to action ── */}
        <section className="s" aria-labelledby="cta-t">
          <div className="wrap">
            <div className="cta">
              <h2 id="cta-t">Put every job on one board</h2>
              <p>
                See Dispatch with your own job types and areas. Thirty minutes, on a call or in person, and you will
                know whether it fits.
              </p>
              <div className="actions">
                <a className="btn" href="#book">
                  Book a demo <ArrowRight size={16} aria-hidden="true" />
                </a>
                <a className="btn ghost" href={CUSTOMER_CARE.tel}>
                  Call {CUSTOMER_CARE.display}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="foot">
        <div className="wrap">
          <div className="foot-grid">
            <div>
              <span className="foot-label">GoSpeedNet Dispatch</span>
              <p>Field service, billing and contracts for internet providers in Kenya. Built and used by GoSpeedNet.</p>
            </div>
            <div>
              <span className="foot-label">Contact</span>
              <ul>
                <li>
                  <a href={CUSTOMER_CARE.tel}>Customer care: {CUSTOMER_CARE.display}</a>
                </li>
                <li>
                  <a href={CUSTOMER_CARE.whatsapp} target="_blank" rel="noopener noreferrer">
                    WhatsApp us
                  </a>
                </li>
                <li>
                  <a href={COMPANY_SITE}>gospeednet.co.ke</a>
                </li>
                <li>
                  <a href={`${COMPANY_SITE}/contact`}>Report a fault</a>
                </li>
              </ul>
            </div>
            <div>
              <span className="foot-label">Product</span>
              <ul>
                <li>
                  <a href="#features">Features</a>
                </li>
                <li>
                  <a href="#demo">Demo</a>
                </li>
                <li>
                  <a href="#pricing">Pricing</a>
                </li>
                <li>
                  <a href="#track">Track a job</a>
                </li>
              </ul>
            </div>
            <div>
              <span className="foot-label">Staff</span>
              <ul>
                <li>
                  <Link to="/login">Staff sign in</Link>
                </li>
              </ul>
              <span className="foot-label foot-gap">Theme</span>
              <ThemeSwitch theme={theme} onChange={setTheme} id="theme-foot" />
            </div>
          </div>
          <div className="foot-base">
            <span>© {new Date().getFullYear()} GoSpeedNet</span>
            <span>Prices in KES · Payment details on every invoice</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
